import React, { useState } from 'react';
import { Button, Input, Dropdown, Option, Text } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowRightRegular } from '@fluentui/react-icons';
import './styles.scss';

interface PaginatorProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    itemsPerPage?: number;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    totalItems?: number;
    currentItems?: number;
    showItemsPerPage?: boolean;
}

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const Paginator: React.FC<PaginatorProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    className = '',
    itemsPerPage = 10,
    onItemsPerPageChange,
    totalItems = 0,
    currentItems = 0,
    showItemsPerPage = false
}) => {
    const [inputPage, setInputPage] = useState('');
    const [activeInputIndex, setActiveInputIndex] = useState<number | null>(null);

    const getPageNumbers = () => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const delta = 1;
        const range: number[] = [];
        const rangeWithDots: (number | string)[] = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i);
        }

        if (currentPage - delta > 2) {
            rangeWithDots.push(1, '...');
        } else {
            rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push('...', totalPages);
        } else if (totalPages > 1) {
            rangeWithDots.push(totalPages);
        }

        return rangeWithDots;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            setInputPage(value);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const page = parseInt(inputPage);
            if (page >= 1 && page <= totalPages) {
                onPageChange(page);
                setActiveInputIndex(null);
                setInputPage('');
            }
        } else if (e.key === 'Escape') {
            setActiveInputIndex(null);
            setInputPage('');
        }
    };

    const handleInputBlur = () => {
        setActiveInputIndex(null);
        setInputPage('');
    };

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        onItemsPerPageChange?.(newItemsPerPage);
        onPageChange(1);
    };

    if (totalPages <= 1 && !showItemsPerPage) return null;

    return (
        <div className={showItemsPerPage ? `paginator expanded ${className}` : `paginator ${className}`}>

            {showItemsPerPage && (
                <div className="paginator__total-items">
                    <Text>{currentItems}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}</Text>
                </div>
            )}


            {(totalPages > 1 || showItemsPerPage) && (
                <div className="paginator__navigation">
                    <Button
                        size="small"
                        className='paginator__prev'
                        icon={<ArrowLeftRegular />}
                        appearance="transparent"
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(currentPage - 1)}
                    />

                    <div className="paginator__numbers">
                        {getPageNumbers().map((pageNumber, index) => (
                            typeof pageNumber === 'number' ? (
                                <Button
                                    size="small"
                                    key={index}
                                    appearance='transparent'
                                    onClick={() => onPageChange(pageNumber)}
                                    className={pageNumber === currentPage ? 'active' : ''}
                                >
                                    {pageNumber}
                                </Button>
                            ) : (
                                <span key={index} className="paginator__dots" onClick={() => setActiveInputIndex(index)}>
                                    {activeInputIndex === index ? (
                                        <Input
                                            size="small"
                                            autoFocus
                                            value={inputPage}
                                            onChange={handleInputChange}
                                            onKeyDown={handleInputKeyDown}
                                            onBlur={handleInputBlur}
                                            className="paginator__input"
                                        />
                                    ) : pageNumber}
                                </span>
                            )
                        ))}
                    </div>

                    <Button
                        size="small"
                        className='paginator__next'
                        icon={<ArrowRightRegular />}
                        appearance="transparent"
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                    />
                </div>
            )}

            {showItemsPerPage && (
                <div className="paginator__items-per-page">
                    <Text className="items-per-page-text">Items per page: </Text>
                    <Dropdown
                        appearance='underline'
                        value={itemsPerPage.toString()}
                        onOptionSelect={(_, data) => handleItemsPerPageChange(Number(data.optionValue))}
                    >
                        {ITEMS_PER_PAGE_OPTIONS.map(value => (
                            <Option key={value} value={value.toString()} text={value.toString()}>
                                {value}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
            )}
        </div>
    );
};

export default Paginator;
